import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, setToken } from "../lib/api";
import { credits } from "../lib/format";
import Mark from "./Mark";

export default function Shell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.auth.me });
  const { data: billing } = useQuery({ queryKey: ["billing"], queryFn: api.billing.get });

  async function signOut() {
    try {
      await api.auth.signOut();
    } finally {
      // Clear the token even if the request failed. The person asked to be
      // signed out; leaving a working token because the network blipped is the
      // wrong side to fail on.
      setToken(null);
      navigate("/signin");
    }
  }

  const nav = [
    { to: "/app", label: "Projects", end: true },
    { to: "/app/billing", label: "Billing" },
    { to: "/app/settings", label: "Settings" },
    ...(me?.user.platform_admin ? [{ to: "/app/admin", label: "Admin" }] : []),
  ];

  return (
    <div className="relative flex min-h-full flex-col">
      {/* The same clay bloom as the marketing pages, at a fraction of the
          intensity. Enough that the console reads as part of one product. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[32rem]"
        style={{
          background:
            "radial-gradient(60% 100% at 50% 0%, rgb(var(--clay) / 0.07) 0%, transparent 70%)",
        }}
      />
      <header className="sticky top-0 z-10 border-b border-hairline bg-paper/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-5 py-3">
          <NavLink to="/app" className="flex items-center gap-2">
            <Mark />
            <span className="text-sm font-semibold tracking-tight">Atelier</span>
          </NavLink>

          <nav className="flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `rounded-card px-3 py-1.5 text-sm transition ${
                    isActive ? "bg-hairline/60 font-medium" : "text-muted hover:text-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3 text-xs">
            {billing && (
              <NavLink to="/app/billing" className="tabular-nums text-muted hover:text-ink">
                {credits(billing.balance)} credits
              </NavLink>
            )}
            {me && (
              <span
                className="hidden rounded-full border border-hairline px-2.5 py-1 text-muted sm:inline"
                title={me.user.email}
              >
                {me.tenant.name}
              </span>
            )}
            <button onClick={signOut} className="text-muted transition hover:text-ink">
              Sign out
            </button>
          </div>
        </div>

        {me?.tenant.suspended && (
          // A suspended org can still read and export. Saying why, at the top
          // of every page, beats a 402 on the next click with no explanation.
          <div className="border-t border-rust/30 bg-rust/10 px-5 py-2 text-center text-xs text-rust">
            This organisation is suspended{me.tenant.suspended_reason ? `: ${me.tenant.suspended_reason}` : "."} You
            can still read and download your code.
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-10">{children}</main>

      <footer className="border-t border-hairline px-5 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between text-2xs text-muted">
          <span>Atelier</span>
          <span>Every app ships with health checks, metrics, CI and an automatic rollback.</span>
        </div>
      </footer>
    </div>
  );
}
